import React, { useState } from 'react';
import ReactSelect5, { ReactSelect5Props } from '@/components/common/ReactSelect5';
import cn from '@/utils/cn';

export type Option = { label: string | React.ReactNode, value: any };

// AIDEV-NOTE: `name` can't double as the input id — the section builder renders one dropdown per
// reading tab, all submitting `section[theme_id]`, so htmlFor would bind every label to the first
// input. React 16 has no useId, hence the counter.
let instanceCount = 0;
const nextInputId = () => {
  instanceCount += 1;

  return `section-editor-dropdown-${instanceCount}`;
};

interface SectionEditorDropdownProps<T extends Option> {
  className?: string;
  initialValue: T['value'];
  label: string;
  labelClassName?: string;
  name: string;
  options: T[];
  onChange: (value: T['value']) => any;
  SelectProps?: ReactSelect5Props;
}

const SectionEditorDropdown = <T extends Option>({
  className,
  initialValue,
  label,
  labelClassName = '',
  name,
  onChange,
  options,
  SelectProps = {},
}: SectionEditorDropdownProps<T>) => {
  // react-select gives its input an id only when inputId is set, so without this the visible label
  // resolves to nothing (WCAG 1.3.1 / 4.1.2).
  const [inputId] = useState(nextInputId);

  const handleChange = ({ value }: T) => {
    onChange(value);
  };

  const defaultValue = options.find(opt => opt.value === initialValue);

  return (
    <div className={cn('tw-flex tw-items-center input !tw-overflow-visible', className)}>
      <label className={cn('!tw-p-0', labelClassName)} htmlFor={inputId}>
        {label}
      </label>
      <div className="tw-w-[230px] tw-inline-block tw-overflow-visible">
        <ReactSelect5
          defaultValue={defaultValue}
          inputId={inputId}
          name={name}
          options={options}
          onChange={handleChange}
          {...SelectProps}
        />
      </div>
    </div>
  );
};

export default SectionEditorDropdown;
